// src/pages/admin/UnitManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit, Trash2, ChevronRight, ChevronDown,
  Building2, GraduationCap, FlaskConical, BookOpen,
  FolderTree, Shield, Users, AlertTriangle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select, Loading, LoadingLabel } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import { Unit, User } from '../../types';

// Extended Unit interface to include potential leader relation if API returns it, or we map it
interface UnitWithLeader extends Unit {
  leader?: User;
}

const typeIcons: Record<string, React.ReactNode> = {
  UNIVERSITAS: <Building2 className="w-4 h-4" />,
  FAKULTAS: <Building2 className="w-4 h-4" />,
  PRODI: <GraduationCap className="w-4 h-4" />,
  PUSAT: <FlaskConical className="w-4 h-4" />,
  LEMBAGA: <BookOpen className="w-4 h-4" />,
  BAGIAN: <FolderTree className="w-4 h-4" />,
  LAB: <FlaskConical className="w-4 h-4" />,
  WAREK: <Shield className="w-4 h-4" />,
  TU: <Users className="w-4 h-4" />,
};

// ✅ Hierarki unit yang valid sesuai struktur UBP Karawang (Moved to Top for better access)
const VALID_PARENT_CHILD: Record<string, string[]> = {
  'UNIVERSITAS': ['FAKULTAS', 'WAREK', 'LEMBAGA', 'BAGIAN'],
  'WAREK': ['BAGIAN'],
  'FAKULTAS': ['PRODI', 'TU', 'LAB'],
  'LEMBAGA': ['PUSAT'],
  'BAGIAN': ['TU'],
  'PRODI': [],
  'PUSAT': [],
  'TU': [],
  'LAB': [],
};

// ✅ Deskripsi hierarki untuk ditampilkan ke user
const HIERARCHY_INFO: Record<string, { parent: string; description: string }> = {
  'UNIVERSITAS': { parent: '-', description: 'Unit tertinggi, hanya boleh satu' },
  'WAREK': { parent: 'UNIVERSITAS', description: 'Wakil Rektor, langsung di bawah Universitas' },
  'FAKULTAS': { parent: 'UNIVERSITAS', description: 'Fakultas, di bawah Universitas' },
  'LEMBAGA': { parent: 'UNIVERSITAS', description: 'Lembaga, di bawah Universitas' },
  'PRODI': { parent: 'FAKULTAS', description: 'Program Studi, di bawah Fakultas' },
  'TU': { parent: 'FAKULTAS', description: 'Tata Usaha, di bawah Fakultas' },
  'LAB': { parent: 'FAKULTAS', description: 'Laboratorium, di bawah Fakultas' },
  'PUSAT': { parent: 'LEMBAGA', description: 'Pusat/UPT, di bawah Lembaga' },
  'BAGIAN': { parent: 'UNIVERSITAS/WAREK', description: 'Bagian, di bawah Universitas atau Warek' },
};

// Soft colored badges with border
const typeBadgeColors: Record<string, string> = {
  UNIVERSITAS: 'text-teal-700 bg-teal-50 border-teal-200',
  FAKULTAS: 'text-blue-700 bg-blue-50 border-blue-200',
  PRODI: 'text-purple-700 bg-purple-50 border-purple-200',
  TU: 'text-cyan-700 bg-cyan-50 border-cyan-200',
  PUSAT: 'text-orange-700 bg-orange-50 border-orange-200',
  LEMBAGA: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  BAGIAN: 'text-gray-700 bg-gray-100 border-gray-200',
  LAB: 'text-pink-700 bg-pink-50 border-pink-200',
  WAREK: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

// Solid colors for stat cards
const typeCardColors: Record<string, string> = {
  UNIVERSITAS: 'text-teal-700 bg-teal-50 border-teal-200',
  FAKULTAS: 'text-blue-700 bg-blue-50 border-blue-200',
  PRODI: 'text-purple-700 bg-purple-50 border-purple-200',
  TU: 'text-cyan-700 bg-cyan-50 border-cyan-200',
  PUSAT: 'text-orange-700 bg-orange-50 border-orange-200',
  LEMBAGA: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  BAGIAN: 'text-gray-700 bg-gray-100 border-gray-200',
  LAB: 'text-pink-700 bg-pink-50 border-pink-200',
  WAREK: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

const typeOptions = [
  { value: 'UNIVERSITAS', label: 'Universitas' },
  { value: 'FAKULTAS', label: 'Fakultas' },
  { value: 'PRODI', label: 'Program Studi' },
  { value: 'TU', label: 'Tata Usaha' },
  { value: 'PUSAT', label: 'Pusat/UPT' },
  { value: 'LEMBAGA', label: 'Lembaga' },
  { value: 'BAGIAN', label: 'Bagian' },
  { value: 'LAB', label: 'Laboratorium' },
  { value: 'WAREK', label: 'Wakil Rektor' },
];

// Tree Node Component — redesigned with card-style rows + dashed connectors
const TreeNode: React.FC<{
  node: UnitWithLeader;
  onEdit: (node: UnitWithLeader) => void;
  onDelete: (node: UnitWithLeader) => void;
  onAddChild: (parentNode: UnitWithLeader) => void;
  depth?: number;
  usersMap: Record<number, string>;
  isLast?: boolean;
}> = ({ node, onEdit, onDelete, onAddChild, depth = 0, usersMap, isLast = false }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const indent = depth * 36;

  return (
    <div className="relative">
      {/* Dashed vertical line from parent */}
      {depth > 0 && (
        <div
          className="absolute border-l-2 border-dashed border-neutral-border/50"
          style={{
            top: 0,
            bottom: isLast ? '55%' : 0,
            left: `${indent - 18}px`,
          }}
        />
      )}

      <div
        className="relative flex items-center gap-3 mb-2"
        style={{ paddingLeft: `${indent}px` }}
      >
        {/* Horizontal connector */}
        {depth > 0 && (
          <div
            className="absolute border-t-2 border-dashed border-neutral-border/50"
            style={{
              top: '50%',
              left: `${indent - 18}px`,
              width: '18px',
            }}
          />
        )}

        {/* Row card */}
        <div
          className={`
            flex-1 flex items-center gap-3 py-2.5 px-4 rounded-xl border
            hover:shadow-md hover:border-teal-main/30 hover:bg-teal-main/[0.02]
            transition-all duration-150 group cursor-default
            ${depth === 0
              ? 'bg-gradient-to-r from-teal-50/60 to-white border-teal-main/30 shadow-sm'
              : 'bg-white border-neutral-border/40'}
          `}
        >
          {/* Expand/Collapse button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md transition-colors ${hasChildren
              ? 'text-teal-main hover:bg-teal-main/10'
              : 'invisible'
              }`}
          >
            {expanded
              ? <ChevronDown className="w-4 h-4" />
              : <ChevronRight className="w-4 h-4" />}
          </button>

          {/* Type Badge */}
          <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase font-black border tracking-wide ${typeBadgeColors[node.type] || 'text-gray-700 bg-gray-100 border-gray-200'}`}>
            {typeIcons[node.type]}
            {node.type}
          </span>

          {/* Name & Code */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className={`text-sm font-bold truncate ${depth === 0 ? 'text-teal-dark' : 'text-neutral-text-primary'}`}>
              {node.name}
            </span>
            <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded bg-neutral-bg border border-neutral-border/60 text-neutral-text-secondary">
              {node.code}
            </span>
          </div>

          {/* Leader */}
          {node.leader_id && (
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-bg border border-neutral-border/50 text-xs text-neutral-text-secondary">
              <Users className="w-3 h-3 text-teal-main flex-shrink-0" />
              <span className="truncate max-w-[120px]" title={`Pimpinan: ${usersMap[node.leader_id]}`}>
                {usersMap[node.leader_id] || `ID: ${node.leader_id}`}
              </span>
            </div>
          )}

          {node.secretary_id && (
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-bg border border-neutral-border/50 text-xs text-neutral-text-secondary">
              <Users className="w-3 h-3 text-cyan-600 flex-shrink-0" />
              <span className="truncate max-w-[120px]" title={`Ka TU/Sekretaris: ${usersMap[node.secretary_id]}`}>
                {usersMap[node.secretary_id] || `ID: ${node.secretary_id}`}
              </span>
            </div>
          )}

          {/* Status */}
          <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-1 rounded-md border ${node.is_active
            ? 'bg-success/10 text-success border-success/20'
            : 'bg-danger/10 text-danger border-danger/20'
            }`}>
            {node.is_active ? 'Aktif' : 'Nonaktif'}
          </span>

          {/* Actions — visible on hover */}
          <div className="flex-shrink-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onEdit(node)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-500 text-blue-600 hover:text-white text-[11px] font-bold transition-colors border border-blue-200 hover:border-blue-500"
              title="Edit unit ini"
            >
              <Edit className="w-3.5 h-3.5" />
              Edit
            </button>
            {/* ✅ FIX: Hanya tampilkan tombol + jika tipe bisa punya sub-unit */}
            {(VALID_PARENT_CHILD[node.type] || []).length > 0 && (
              <button
                onClick={() => onAddChild(node)}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-main text-teal-main hover:text-white text-[11px] font-bold transition-colors border border-teal-200 hover:border-teal-main"
                title={`Tambah sub-unit (${(VALID_PARENT_CHILD[node.type] || []).join(', ')})`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            {(!node.children || node.children.length === 0) && (
              <button
                onClick={() => onDelete(node)}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-red-50 hover:bg-danger text-danger hover:text-white text-[11px] font-bold transition-colors border border-red-200 hover:border-danger"
                title="Hapus unit ini"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children?.map((child, index) => (
            <TreeNode
              key={child.id}
              node={child}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              depth={depth + 1}
              usersMap={usersMap}
              isLast={index === (node.children?.length || 0) - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UnitManagement: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<UnitWithLeader[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});

  const [editUnit, setEditUnit] = useState<UnitWithLeader | null>(null);
  const [parentUnit, setParentUnit] = useState<UnitWithLeader | null>(null);
  const [search, setSearch] = useState('');

  const [formData, setFormData] = useState({
    name: '', code: '', type: 'PRODI' as string, leader_id: '', secretary_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [unitsRes, usersRes] = await Promise.all([
        adminService.units.getAll(),
        adminService.users.getAll({ per_page: 9999 })
      ]);

      const mapChildren = (data: any): UnitWithLeader[] => {
        if (!Array.isArray(data)) return [];
        return data.map((item: any) => ({
          ...item,
          children: item.children_recursive ? mapChildren(item.children_recursive) : (item.children || [])
        }));
      };

      const unitsData = (unitsRes as any)?.data || unitsRes;
      const mappedUnits = mapChildren(unitsData);
      setUnits(mappedUnits);

      const usersData = (usersRes as any)?.data || usersRes;
      const usersList = Array.isArray(usersData) ? usersData : (usersData?.data || []);
      setUsers(usersList);

      const map: Record<number, string> = {};
      usersList.forEach((u: User) => map[u.id] = u.name);
      setUsersMap(map);

    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat data unit');
    } finally {
      setLoading(false);
    }
  };

  const totalUnits = (() => {
    let count = 0;
    const countNodes = (nodes: Unit[]) => {
      nodes.forEach((n) => {
        count++;
        if (n.children) countNodes(n.children);
      });
    };
    countNodes(units);
    return count;
  })();

  const countByType = (type: string): number => {
    let count = 0;
    const countType = (nodes: Unit[]) => {
      nodes.forEach((n) => {
        if (n.type === type) count++;
        if (n.children) countType(n.children);
      });
    };
    countType(units);
    return count;
  };


  const handleDelete = async (unit: UnitWithLeader) => {
    // CHECK USERS
    const usersInUnit = users.filter(u => (u as any).unit?.id === unit.id);
    if (usersInUnit.length > 0) {
      toast.error(`Unit "${unit.name}" masih memiliki ${usersInUnit.length} user. Pindahkan user terlebih dahulu.`);
      return;
    }

    // CHECK SUB-UNITS
    if (unit.children && unit.children.length > 0) {
      toast.error(`Unit "${unit.name}" masih memiliki sub-unit. Hapus sub-unit terlebih dahulu.`);
      return;
    }

    if (window.confirm(`Hapus unit "${unit.name}"?`)) {
      try {
        await adminService.units.delete(unit.id);
        toast.success('Unit berhasil dihapus');
        fetchData();
      } catch (e: any) {
        toast.error(e.response?.data?.message || 'Gagal menghapus unit');
      }
    }
  };

  const openCreate = (parent?: UnitWithLeader) => {
    setEditUnit(null);
    setParentUnit(parent || null);

    let defaultType = 'UNIVERSITAS';

    if (parent) {
      const allowedChildren = VALID_PARENT_CHILD[parent.type] || [];
      defaultType = allowedChildren.length > 0 ? allowedChildren[0] : '';

      // ✅ ADD: Debug log untuk verify state
    }

    setFormData({
      name: '',
      code: '',
      type: defaultType,
      leader_id: '',
      secretary_id: ''
    });
    setShowModal(true);
  };

  const openEdit = (unit: UnitWithLeader) => {
    setEditUnit(unit);

    // ✅ FIX: Cari parent unit untuk menampilkan info di modal
    const findParent = (nodes: UnitWithLeader[], targetId: number): UnitWithLeader | null => {
      for (const node of nodes) {
        if (node.children) {
          for (const child of node.children) {
            if (child.id === targetId) return node;
          }
          const found = findParent(node.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const parent = findParent(units, unit.id);
    setParentUnit(parent);

    setFormData({
      name: unit.name,
      code: unit.code,
      type: unit.type,
      leader_id: unit.leader_id ? unit.leader_id.toString() : '',
      secretary_id: unit.secretary_id ? unit.secretary_id.toString() : '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nama unit wajib diisi');
      return;
    }
    if (!formData.code.trim()) {
      toast.error('Kode unit wajib diisi');
      return;
    }
    if (formData.code.trim().length < 2) {
      toast.error('Kode unit minimal 2 karakter');
      return;
    }
    if (!formData.type) {
      toast.error('Tipe unit wajib dipilih');
      return;
    }

    // Validasi parent-child type
    if (!editUnit && parentUnit) {
      const allowedChildren = VALID_PARENT_CHILD[parentUnit.type] || [];
      if (!allowedChildren.includes(formData.type)) {
        const hierarchy = HIERARCHY_INFO[formData.type];
        toast.error(
          `Tipe "${formData.type}" tidak bisa menjadi sub-unit dari "${parentUnit.type}".\n` +
          `Seharusnya di bawah: ${hierarchy?.parent || '-'}.\n` +
          `${parentUnit.type} hanya boleh memiliki: ${allowedChildren.join(', ') || 'Tidak ada'}.`,
          { duration: 6000 }
        );
        return;
      }
    }

    // Validasi root unit
    if (!editUnit && !parentUnit) {
      if (formData.type !== 'UNIVERSITAS') {
        toast.error(
          `Tipe "${formData.type}" harus memiliki parent unit.\n` +
          `Gunakan tombol + pada unit induk untuk menambah sub-unit.`,
          { duration: 5000 }
        );
        return;
      }

      const hasUniversitas = units.some(u => u.type === 'UNIVERSITAS');
      if (hasUniversitas) {
        toast.error('Hanya boleh ada satu unit dengan tipe UNIVERSITAS');
        return;
      }
    }

    // ✅ ADD: Validasi leader wajib untuk unit kritis
    const leaderRequiredTypes = ['UNIVERSITAS', 'FAKULTAS', 'WAREK', 'LEMBAGA'];
    if (leaderRequiredTypes.includes(formData.type) && !formData.leader_id) {
      toast.error(
        `Unit tipe ${formData.type} WAJIB memiliki pimpinan.\n` +
        `Pimpinan digunakan sebagai validator dalam workflow.`
      );
      return;
    }

    setSaving(true);
    try {
      // ✅ FIX: Separated payloads and parent behavior
      if (editUnit) {
        // Saat edit, JANGAN kirim parent_id (tidak boleh pindah parent via edit)
        const editPayload = {
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
          type: formData.type,
          leader_id: formData.leader_id ? Number(formData.leader_id) : null,
          secretary_id: formData.secretary_id ? Number(formData.secretary_id) : null,
        };
        await adminService.units.update(editUnit.id, editPayload);
        toast.success('Unit berhasil diperbarui');
      } else {
        // Saat create baru
        const createPayload: any = {
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
          type: formData.type,
          leader_id: formData.leader_id ? Number(formData.leader_id) : null,
          secretary_id: formData.secretary_id ? Number(formData.secretary_id) : null,
        };

        if (parentUnit) {
          createPayload.parent_id = parentUnit.id;

          // ✅ Debug log
        }

        await adminService.units.create(createPayload);
        toast.success('Unit berhasil ditambahkan');
      }

      setShowModal(false);
      fetchData();
    } catch (e: any) {
      console.error('Save unit error:', e);

      const status = e?.response?.status;
      const serverMessage = e?.response?.data?.message || '';
      const serverErrors = e?.response?.data?.errors;

      if (status === 422 && serverErrors) {
        const errorMessages: string[] = [];

        if (serverErrors.code) {
          errorMessages.push(
            `Kode "${formData.code}" sudah digunakan. Silakan gunakan kode lain.`
          );
        }
        if (serverErrors.name) {
          errorMessages.push(`Nama: ${Array.isArray(serverErrors.name) ? serverErrors.name[0] : serverErrors.name}`);
        }

        if (errorMessages.length === 0) {
          Object.entries(serverErrors).forEach(([field, msgs]) => {
            const msg = Array.isArray(msgs) ? msgs[0] : msgs;
            errorMessages.push(`${field}: ${msg}`);
          });
        }

        toast.error(errorMessages.join('\n'), { duration: 6000 });
      } else if (status === 500 && serverMessage.includes('Duplicate entry')) {
        // ✅ ADD: Handle 500 duplicate entry error
        const match = serverMessage.match(/Duplicate entry '([^']+)'/);
        const duplicateCode = match ? match[1] : formData.code;
        toast.error(
          `Kode "${duplicateCode}" sudah ada di database (mungkin dari data yang pernah dihapus).\n\nGunakan kode lain.`,
          { duration: 6000 }
        );
      } else {
        toast.error(serverMessage || 'Gagal menyimpan unit');
      }
    } finally {
      setSaving(false);
    }
  };

  const filterUnits = (nodes: UnitWithLeader[], query: string): UnitWithLeader[] => {
    if (!query) return nodes;
    return nodes.reduce((acc: UnitWithLeader[], node) => {
      const matches = node.name.toLowerCase().includes(query.toLowerCase()) || node.code.toLowerCase().includes(query.toLowerCase());
      const filteredChildren = node.children ? filterUnits(node.children, query) : [];

      if (matches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      return acc;
    }, []);
  };

  const displayedUnits = filterUnits(units, search);

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Unit Organisasi' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Struktur Unit Organisasi</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <span className="font-bold text-teal-main">{totalUnits}</span> unit terdaftar dalam hierarki organisasi
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => openCreate()} icon={<Plus className="w-4 h-4" />}>
          Tambah Unit
        </Button>
      </div>

      {/* Stats — redesigned coloured stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
        {typeOptions.map((t) => {
          const count = countByType(t.value);
          return (
            <div
              key={t.value}
              className={`relative overflow-hidden rounded-xl border p-3 flex flex-col gap-1 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-default ${typeCardColors[t.value] || 'text-gray-700 bg-gray-50 border-gray-200'}`}
            >
              {/* Watermark icon */}
              <div className="absolute -right-3 -top-3 opacity-10 pointer-events-none scale-150">
                {typeIcons[t.value]}
              </div>
              <div className="flex items-center justify-between gap-1">
                <div className="p-1.5 rounded-lg bg-white/60 shadow-sm backdrop-blur-sm">
                  {typeIcons[t.value]}
                </div>
                <span className="text-2xl font-black">{count}</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-75 leading-tight">{t.label}</p>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
        <input
          type="text"
          placeholder="Cari unit berdasarkan nama atau kode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-neutral-border text-sm bg-white focus:outline-none focus:border-teal-main focus:ring-0 shadow-sm transition-colors"
        />
      </div>

      {/* Tree View */}
      <Card padding="md" className="shadow-sm">
        {/* Card header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-neutral-border/60">
          <h2 className="text-base font-bold text-neutral-text-primary flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-main/10 flex items-center justify-center">
              <FolderTree className="w-4 h-4 text-teal-main" />
            </div>
            Hierarki Organisasi
          </h2>
          {search && (
            <span className="text-xs text-neutral-text-secondary">
              Menampilkan hasil untuk <strong>"{search}"</strong>
            </span>
          )}
        </div>

        {/* Tree content */}
        {loading ? (
          <Loading variant="inline" message="Memuat struktur organisasi..." className="py-8" />
        ) : displayedUnits.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mx-auto mb-3">
              <FolderTree className="w-8 h-8 text-neutral-text-tertiary" />
            </div>
            <p className="text-base font-bold text-neutral-text-primary">Tidak ada unit ditemukan</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {search ? 'Coba kata kunci pencarian yang lain.' : 'Belum ada unit dalam sistem.'}
            </p>
          </div>
        ) : (
          <div className="pt-1">
            {displayedUnits.map((unit, index) => (
              <TreeNode
                key={unit.id}
                node={unit}
                onEdit={openEdit}
                onDelete={handleDelete}
                onAddChild={(parent) => openCreate(parent)}
                usersMap={usersMap}
                isLast={index === displayedUnits.length - 1}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editUnit ? 'Edit Unit' : 'Tambah Unit Baru'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? <LoadingLabel label="Menyimpan..." /> : 'Simpan'}</Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* ✅ Info context: Edit atau Create */}
          {editUnit ? (
            // EDIT MODE
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-xs font-semibold text-amber-700 mb-1">
                Mengedit unit:
              </p>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${typeBadgeColors[editUnit.type] || 'text-gray-700 bg-gray-100 border-gray-200'
                  }`}>
                  {editUnit.type}
                </span>
                <p className="text-sm font-bold text-amber-900">
                  {editUnit.name}
                </p>
                <span className="text-xs text-neutral-text-secondary">
                  ({editUnit.code})
                </span>
              </div>
              {parentUnit && (
                <p className="text-xs text-amber-600 mt-2">
                  Berada di bawah: <strong>{parentUnit.name}</strong> ({parentUnit.type})
                </p>
              )}
              {!parentUnit && (
                <p className="text-xs text-amber-600 mt-2">
                  Unit root (tidak memiliki parent)
                </p>
              )}
            </div>
          ) : parentUnit ? (
            // CREATE dengan parent
            <div className="p-3 bg-teal-main/5 rounded-xl border border-teal-main/20">
              <p className="text-xs font-semibold text-teal-main mb-1">
                Menambah sub-unit di bawah:
              </p>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${typeBadgeColors[parentUnit.type] || 'text-gray-700 bg-gray-100 border-gray-200'
                  }`}>
                  {parentUnit.type}
                </span>
                <p className="text-sm font-bold text-teal-dark">
                  {parentUnit.name}
                </p>
                <span className="text-xs text-neutral-text-secondary">
                  ({parentUnit.code})
                </span>
              </div>
              <p className="text-xs text-neutral-text-secondary mt-2">
                Tipe yang diizinkan: <strong>{(VALID_PARENT_CHILD[parentUnit.type] || []).join(', ') || 'Tidak ada'}</strong>
              </p>
            </div>
          ) : (
            // CREATE tanpa parent
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-xs text-blue-700">
                Membuat unit root. Hanya tipe <strong>UNIVERSITAS</strong> yang diizinkan.
              </p>
            </div>
          )}

          <div className="p-3 bg-neutral-bg rounded-xl border border-neutral-border">
            <p className="text-xs font-semibold text-neutral-text-primary mb-1">
              Catatan Struktur Unit
            </p>
            <p className="text-xs text-neutral-text-secondary leading-relaxed">
              Path unit disusun otomatis oleh sistem berdasarkan unit induk yang dipilih.
              Admin cukup menentukan struktur parent-child dan pimpinan unit dengan benar.
            </p>
          </div>

          {/* Hierarki Reference */}
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-xs font-semibold text-blue-800 mb-2">
              Struktur Hierarki UBP Karawang
            </p>
            <div className="text-[10px] text-blue-700 font-mono leading-relaxed">
              <div>Universitas</div>
              <div className="ml-4">|- Wakil Rektor -&gt; Bagian</div>
              <div className="ml-4">|- Fakultas -&gt; Prodi, TU, Lab</div>
              <div className="ml-4">|- Lembaga -&gt; Pusat/UPT</div>
              <div className="ml-4">`- Bagian -&gt; TU</div>
            </div>
          </div>

          <Input
            label="Nama Unit"
            placeholder="Contoh: Prodi Teknik Informatika"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Kode Unit"
            placeholder="Contoh: TI"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
            helpText="Kode unik, minimal 2 karakter. Akan otomatis menjadi huruf besar."
          />

          {/* ✅ FIX: Type select with filtered options for both Edit and Create */}
          <div>
            <Select
              label="Tipe Unit"
              options={(() => {
                // ✅ Saat EDIT: filter berdasarkan parent unit saat ini
                if (editUnit) {
                  // Jika unit punya parent (didapat dari openEdit)
                  if (parentUnit) {
                    const allowed = VALID_PARENT_CHILD[parentUnit.type] || [];
                    return allowed.map(t => {
                      const opt = typeOptions.find(o => o.value === t);
                      return opt || { value: t, label: t };
                    });
                  } else {
                    // Root unit → hanya UNIVERSITAS
                    return typeOptions.filter(t => t.value === 'UNIVERSITAS');
                  }
                }

                // ✅ Saat TAMBAH dengan parent
                if (parentUnit) {
                  const allowed = VALID_PARENT_CHILD[parentUnit.type] || [];
                  return allowed.map(t => {
                    const opt = typeOptions.find(o => o.value === t);
                    return opt || { value: t, label: t };
                  });
                }

                // ✅ Saat TAMBAH tanpa parent → hanya UNIVERSITAS
                return typeOptions.filter(t => {
                  if (t.value === 'UNIVERSITAS') {
                    return !units.some(u => u.type === 'UNIVERSITAS');
                  }
                  return false;
                });
              })()}
              value={formData.type}
              onChange={(v) => setFormData({ ...formData, type: v })}
              required
            />
            {/* ✅ Info tentang tipe yang dipilih */}
            {formData.type && HIERARCHY_INFO[formData.type] && (
              <p className="text-xs text-neutral-text-secondary mt-1.5">
                ℹ️ {HIERARCHY_INFO[formData.type].description}
              </p>
            )}
          </div>
          <div className="space-y-4 pt-2 border-t border-neutral-border">
            {/* Warning untuk unit yang WAJIB punya leader */}
            {['UNIVERSITAS', 'FAKULTAS', 'WAREK', 'LEMBAGA'].includes(formData.type) && (
              <div className="flex items-start gap-2 p-2.5 bg-danger/5 rounded-lg border border-danger/20">
                <AlertTriangle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
                <p className="text-[11px] text-danger">
                  <strong>Wajib!</strong> Unit tipe <strong>{formData.type}</strong> harus memiliki pimpinan 
                  karena digunakan sebagai validator dalam workflow.
                </p>
              </div>
            )}

            <Select
              label={
                ['UNIVERSITAS', 'FAKULTAS', 'WAREK', 'LEMBAGA'].includes(formData.type)
                  ? 'Pimpinan (Leader) — WAJIB *'
                  : 'Pimpinan (Leader) — Opsional'
              }
              options={[
                { value: '', label: 'Pilih Pimpinan' },
                ...users
                  .filter(u => {
                    if (!u.is_active) return false;
                    
                    // ✅ Filter user berdasarkan tipe unit
                    const roleName = u.role?.name?.toLowerCase() || '';
                    
                    switch (formData.type) {
                      case 'UNIVERSITAS':
                        return roleName === 'rektor';
                      case 'WAREK':
                        return roleName.startsWith('wakil_rektor_');
                      case 'FAKULTAS':
                        return roleName === 'dekan';
                      case 'LEMBAGA':
                        return roleName === 'kalem';
                      case 'PRODI':
                        return roleName === 'kaprodi';
                      case 'BAGIAN':
                        return roleName === 'kabag';
                      case 'TU':
                        return roleName === 'ka_tu';
                      case 'LAB':
                        return roleName === 'kalab';
                      case 'PUSAT':
                        return roleName === 'kalem' || roleName === 'kabag';
                      default:
                        return true;
                    }
                  })
                  .map(u => ({ 
                    value: u.id.toString(), 
                    label: `${u.name} — ${u.role?.display_name || u.role?.name || ''}` 
                  }))
              ]}
              value={formData.leader_id}
              onChange={(v) => setFormData({ ...formData, leader_id: v })}
              required={['UNIVERSITAS', 'FAKULTAS', 'WAREK', 'LEMBAGA'].includes(formData.type)}
            />
            {(() => {
              const help = (() => {
                switch (formData.type) {
                  case 'UNIVERSITAS': return 'Pilih user dengan role Rektor';
                  case 'WAREK': return 'Pilih user dengan role Wakil Rektor';
                  case 'FAKULTAS': return 'Pilih user dengan role Dekan';
                  case 'LEMBAGA': return 'Pilih user dengan role Kepala Lembaga';
                  case 'PRODI': return 'Pilih user dengan role Kaprodi (opsional)';
                  case 'BAGIAN': return 'Pilih user dengan role Kabag (opsional)';
                  case 'TU': return 'Pilih user dengan role Ka TU (opsional)';
                  case 'LAB': return 'Pilih user dengan role Kalab (opsional)';
                  default: return '';
                }
              })();
              return help ? (
                <p className="text-[10px] text-neutral-text-secondary mt-1 italic">
                  ℹ️ {help}
                </p>
              ) : null;
            })()}
            
            <Select
              label="Sekretaris / Ka TU — Opsional"
              options={[
                { value: '', label: 'Tidak ada (opsional)' },
                ...users
                  .filter(u => u.is_active)
                  .map(u => ({ 
                    value: u.id.toString(), 
                    label: `${u.name}${u.role?.display_name ? ` — ${u.role.display_name}` : ''}` 
                  }))
              ]}
              value={formData.secretary_id}
              onChange={(v) => setFormData({ ...formData, secretary_id: v })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UnitManagement;
